import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import Iconv2 from './icon-v2/icon';
import { V2_TO_V3_ICON_MAP } from './icon-v2/v2-v3-icons-map';
import { GreenroomState } from '../../stores/types/types';

export * from './icon-v2/colors'
export * from './icon-v2/icons'

interface IconWrapperProps {
	name: string;
	size: number;
	color: string;
	viewBox?: string;
	className?: string;
	forcev3?: boolean;
}

/**
 * IconWrapper component that switches between v2 and v3 icons based on a Redux feature flag.
 *
 * Usage:
 *   <IconWrapper name={ICONS.ADD} size={24} color={COLORS.WHITE} />
 *
 * This component will:
 * - Check the Redux state for the 'useV3Icons' feature flag
 * - If true and a v3 equivalent exists, render the v3 icon
 * - Otherwise, fall back to the v2 Icon component
 */

export default function Icon(props: IconWrapperProps) {
	const {
		name, size, color, viewBox = '0 0 24 24', className = '', forcev3 = false
	} = props;

	// Check Redux for v3 icons feature flag
	const doUseWrapper = useSelector((s: GreenroomState) => s.SettingsReducer.featureFlags?.use_pixel);

	return useMemo(() => {
		// If v3 icons are enabled, try to find the v3 equivalent
		if (doUseWrapper || forcev3) {
			const V3Icon = V2_TO_V3_ICON_MAP[name];
			if (V3Icon) {
				// Render v3 icon with appropriate styling
				// Do not forward viewBox — v3 icons use their own "0 0 24 24" coordinate space
				return (
					<V3Icon
						className={classNames(color, 'icon', className)}
						height={size}
						width={size}
					/>
				);
			}
			// If no v3 equivalent exists, fall through to v2
		}

		// Fall back to v2 icon
		return (
			<Iconv2
				name={name}
				size={size}
				color={color}
				viewBox={viewBox}
				className={className}
			/>
		);
	}, [name, size, color, viewBox, className, doUseWrapper]);
}
